import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2, Calendar } from 'lucide-react';
import { notifyFormSubmission } from '@/lib/notifyForm';

type Lang = 'en' | 'fr' | 'es';

const T = {
  en: {
    title: 'Book a Live SOCRAT Demo',
    sub: 'Get a 30-minute walkthrough on your own perimeter — satellite scan, AI fusion, drill-ready scoring.',
    name: 'Full name', company: 'Company', email: 'Work email', phone: 'Phone (optional)',
    role: 'Role / Title', perimeter: 'Country / target perimeter', when: 'Preferred time',
    notes: 'What you want to see (optional)',
    cta: 'Request my demo', sending: 'Requesting…',
    ok: 'Demo request received',
    okSub: "Our team will reach out within 24h to confirm your slot.",
  },
  fr: {
    title: 'Réservez une démo SOCRAT en direct',
    sub: 'Une session de 30 min sur votre périmètre — scan satellite, fusion IA, scoring prêt-à-forer.',
    name: 'Nom complet', company: 'Société', email: 'Email pro', phone: 'Téléphone (optionnel)',
    role: 'Fonction', perimeter: 'Pays / périmètre cible', when: 'Créneau préféré',
    notes: 'Ce que vous voulez voir (optionnel)',
    cta: 'Réserver ma démo', sending: 'Envoi…',
    ok: 'Demande reçue',
    okSub: 'Notre équipe vous contacte sous 24 h pour confirmer le créneau.',
  },
  es: {
    title: 'Reserva una demo SOCRAT en vivo',
    sub: 'Sesión de 30 min sobre tu perímetro — escaneo satelital, fusión IA, scoring listo para perforar.',
    name: 'Nombre completo', company: 'Empresa', email: 'Email profesional', phone: 'Teléfono (opcional)',
    role: 'Cargo', perimeter: 'País / perímetro objetivo', when: 'Horario preferido',
    notes: 'Qué quieres ver (opcional)',
    cta: 'Solicitar demo', sending: 'Enviando…',
    ok: 'Solicitud recibida',
    okSub: 'Nuestro equipo te contactará en 24 h para confirmar.',
  },
};

interface Props { open: boolean; onClose: () => void; lang?: Lang; }

export default function BookDemoModal({ open, onClose, lang = 'en' }: Props) {
  const t = T[lang];
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', role: '', perimeter: '', when: '', notes: '',
  });
  const u = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      notifyFormSubmission('SOCRAT — Live Demo Booking', form);
      // small delay so the toast feels real
      await new Promise(r => setTimeout(r, 600));
      setDone(true);
    } finally { setSubmitting(false); }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setDone(false); setForm({ name:'', company:'', email:'', phone:'', role:'', perimeter:'', when:'', notes:'' }); }, 250);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
        >
          <motion.div
            initial={{ scale: 0.95, y: 10, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={close} className="absolute top-3 right-3 p-2 rounded-full hover:bg-black/5 z-10" aria-label="close">
              <X className="h-5 w-5 text-sovereign-navy" />
            </button>

            {done ? (
              <div className="p-10 text-center">
                <div className="w-16 h-16 mx-auto bg-sovereign-gold/15 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-sovereign-gold" />
                </div>
                <h3 className="text-2xl font-extrabold text-sovereign-navy mb-2">{t.ok}</h3>
                <p className="text-sovereign-navy/70">{t.okSub}</p>
                <button onClick={close} className="mt-6 px-6 py-2.5 rounded-lg bg-sovereign-navy text-white font-semibold border-2 border-sovereign-gold">OK</button>
              </div>
            ) : (
              <>
                <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-sovereign-navy to-[#0f2847] text-white">
                  <div className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.25em] text-sovereign-gold border border-sovereign-gold/40 rounded-full px-3 py-1 mb-3">
                    <Calendar className="h-3 w-3" /> LIVE DEMO · 30 MIN
                  </div>
                  <h2 className="text-xl md:text-2xl font-extrabold leading-tight">{t.title}</h2>
                  <p className="text-white/80 text-sm mt-1.5">{t.sub}</p>
                </div>
                <form onSubmit={submit} className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t.name + ' *'} value={form.name} onChange={v => u('name', v)} required />
                    <Field label={t.company} value={form.company} onChange={v => u('company', v)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t.email + ' *'} type="email" value={form.email} onChange={v => u('email', v)} required />
                    <Field label={t.phone} value={form.phone} onChange={v => u('phone', v)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t.role} value={form.role} onChange={v => u('role', v)} />
                    <Field label={t.perimeter} value={form.perimeter} onChange={v => u('perimeter', v)} />
                  </div>
                  <Field label={t.when} value={form.when} onChange={v => u('when', v)} placeholder="e.g. Tue 10am CET" />
                  <div>
                    <label className="block text-xs font-semibold text-sovereign-navy mb-1 uppercase tracking-wider">{t.notes}</label>
                    <textarea
                      value={form.notes} onChange={e => u('notes', e.target.value)} rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none"
                    />
                  </div>
                  <button
                    type="submit" disabled={submitting || !form.name || !form.email}
                    className="w-full mt-2 bg-sovereign-navy text-white py-3 rounded-lg font-bold border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? t.sending : (<>{t.cta} <Send className="h-4 w-4" /></>)}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, type = 'text', required, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-sovereign-navy mb-1 uppercase tracking-wider">{label}</label>
      <input
        type={type} required={required} placeholder={placeholder}
        value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none"
      />
    </div>
  );
}
